import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ChatCircleText, Trash, PaperPlaneTilt } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

function fmtDT(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, { hour12: false });
}

export default function RemarksPanel({ vesselId, printMode = false }) {
    const { user } = useAuth();
    const [remarks, setRemarks] = useState([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [posting, setPosting] = useState(false);

    const canRemark = user && ["admin", "manager", "finance", "safety"].includes(user.role);

    const load = async () => {
        try {
            const { data } = await api.get(`/vessels/${vesselId}/remarks`);
            setRemarks(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (vesselId) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vesselId]);

    const submit = async (e) => {
        e.preventDefault();
        if (!text.trim()) return;
        setPosting(true);
        try {
            await api.post(`/vessels/${vesselId}/remarks`, { text });
            setText("");
            await load();
            toast.success("Remark added");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setPosting(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this remark?")) return;
        try {
            await api.delete(`/vessels/${vesselId}/remarks/${id}`);
            setRemarks(remarks.filter((r) => r.id !== id));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    if (printMode) {
        if (remarks.length === 0) return null;
        return (
            <div className="px-5 md:px-10 py-6 md:py-8 border-b border-slate-200" data-testid="cert-remarks-section">
                <div className="uppercase-label text-[var(--grindrod-red)] mb-3">Manager Remarks</div>
                <div className="space-y-3">
                    {remarks.map((r) => (
                        <div key={r.id} className="border-l-4 border-[var(--grindrod-navy)] pl-3">
                            <div className="text-sm text-slate-700 whitespace-pre-wrap">{r.text}</div>
                            <div className="text-[10px] text-slate-500 mt-1">
                                — {r.author_name} ({r.author_role}) · {fmtDT(r.created_at)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="g-card shadow-sm" data-testid="remarks-panel">
            <div className="flex items-stretch">
                <div className="bg-[var(--grindrod-navy)] w-14 flex items-center justify-center">
                    <ChatCircleText size={22} weight="bold" className="text-white" />
                </div>
                <div className="flex-1 bg-[var(--grindrod-navy)] text-white px-5 py-4 flex items-center gap-3 border-b-[3px] border-[var(--grindrod-red)]">
                    <div className="font-display font-black uppercase tracking-wide text-lg">Manager Remarks</div>
                    <span className="ml-auto text-xs font-semibold">{remarks.length} total</span>
                </div>
            </div>

            {canRemark && (
                <form onSubmit={submit} className="p-4 border-b border-slate-100" data-testid="remarks-form">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        className="g-input"
                        rows={2}
                        placeholder="Leave a remark for the record (visible on the performance certificate)…"
                        data-testid="remarks-input"
                    />
                    <div className="flex justify-end mt-2">
                        <button type="submit" className="btn-primary inline-flex items-center gap-2" disabled={posting || !text.trim()} data-testid="remarks-submit">
                            <PaperPlaneTilt size={14} weight="fill" />
                            {posting ? "Posting…" : "Post remark"}
                        </button>
                    </div>
                </form>
            )}

            {loading ? (
                <div className="p-6 text-center text-sm text-slate-400">Loading remarks…</div>
            ) : remarks.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400" data-testid="remarks-empty">
                    No remarks yet.
                </div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {remarks.slice().reverse().map((r) => (
                        <div key={r.id} className="p-4 flex items-start gap-3" data-testid={`remark-${r.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-[var(--grindrod-navy)]">
                                    {r.author_name}
                                    <span className="g-pill g-pill-navy ml-2" style={{ fontSize: 9, padding: "1px 6px" }}>
                                        {r.author_role}
                                    </span>
                                </div>
                                <div className="text-[10px] uppercase-label text-slate-400">{r.author_position} · {fmtDT(r.created_at)}</div>
                                <div className="mt-1.5 text-sm text-slate-700 whitespace-pre-wrap break-words">{r.text}</div>
                            </div>
                            {canRemark && user.id === r.author_id && (
                                <button
                                    onClick={() => remove(r.id)}
                                    className="text-slate-400 hover:text-[var(--grindrod-red)] p-1"
                                    title="Delete remark"
                                    data-testid={`remark-delete-${r.id}`}
                                >
                                    <Trash size={16} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
